import { useState, useEffect, useCallback } from 'react';
import { MedicareBaseRateType } from '../../../../../../services/ConfigurationService/types/centerType';

export interface MedicareBaseRateFormData {
    id: string;
    year: number;
    cbsa: string;
    wageIndex: number | null;
    lrop: number | null;
    nlop: number | null;
    federalCapitalWeight: number | null;
    geographicAdjustmentFactor: number | null;
    inpatientHospitalDeductible: number | null;
}

const INITIAL_FORM_DATA: MedicareBaseRateFormData = {
    id: '',
    year: new Date().getFullYear(),
    cbsa: '',
    wageIndex: null,
    lrop: null,
    nlop: null,
    federalCapitalWeight: null,
    geographicAdjustmentFactor: null,
    inpatientHospitalDeductible: null
};

interface UseMedicareBaseRateFormProps {
    editingRate: MedicareBaseRateType | null | undefined;
    existingRates: MedicareBaseRateType[];
    open: boolean;
}

interface UseMedicareBaseRateFormResult {
    form: MedicareBaseRateFormData;
    errors: { [key: string]: string };
    isEditMode: boolean;
    handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    validateForm: () => boolean;
    resetForm: () => void;
}

const NUMERIC_FIELDS = ['year', 'wageIndex', 'lrop', 'nlop', 'federalCapitalWeight', 'geographicAdjustmentFactor', 'inpatientHospitalDeductible'];

export const useMedicareBaseRateForm = ({
    editingRate,
    existingRates,
    open
}: UseMedicareBaseRateFormProps): UseMedicareBaseRateFormResult => {
    const [form, setForm] = useState<MedicareBaseRateFormData>(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const isEditMode = !!editingRate;

    useEffect(() => {
        if (editingRate) {
            setForm({ ...editingRate });
        } else {
            setForm(INITIAL_FORM_DATA);
        }
        setErrors({});
    }, [editingRate, open]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const isNumericField = NUMERIC_FIELDS.includes(name);

        let processedValue: string | number | null = value;

        if (isNumericField) {
            if (value === '' || value === null) {
                processedValue = name === 'year' ? new Date().getFullYear() : null;
            } else {
                processedValue = parseFloat(value) || null;
            }
        }

        setForm(prevForm => ({
            ...prevForm,
            [name]: processedValue
        }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    }, [errors]);

    const validateForm = useCallback((): boolean => {
        const newErrors: { [key: string]: string } = {};

        // Validate year
        if (!form.year) {
            newErrors.year = 'Year is required';
        } else if (form.year < 1900 || form.year > 2100) {
            newErrors.year = 'Year must be between 1900 and 2100';
        } else if (!isEditMode) {
            // Check for duplicate year+CBSA combination when adding
            const existingRate = existingRates.find(rate => rate.year === form.year && rate.cbsa === form.cbsa);
            if (existingRate) {
                newErrors.year = 'A rate for this year and CBSA already exists';
            }
        } else if (editingRate && (form.year !== editingRate.year || form.cbsa !== editingRate.cbsa)) {
            // Check for duplicate year+CBSA combination when editing and year or CBSA changed
            const existingRate = existingRates.find(rate =>
                rate.id !== editingRate.id &&
                rate.year === form.year &&
                rate.cbsa === form.cbsa
            );
            if (existingRate) {
                newErrors.year = 'A rate for this year and CBSA already exists';
            }
        }

        // Validate CBSA
        if (!form.cbsa) {
            newErrors.cbsa = 'CBSA is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [form, isEditMode, editingRate, existingRates]);

    const resetForm = useCallback(() => {
        setForm(INITIAL_FORM_DATA);
        setErrors({});
    }, []);

    return {
        form,
        errors,
        isEditMode,
        handleChange,
        validateForm,
        resetForm
    };
};
